/**
 * Reordering an administration list by dragging its rows.
 *
 * A list opts in with `data-sortable="<kind>"`, every row carries
 * `data-sortable-id="<primary key>"`. The new order is written to the server
 * as soon as a row is dropped; if that fails the list is put back the way it
 * was, so what is on the screen always matches what is stored.
 *
 * A list which also names the group it stands for, with
 * `data-sortable-parent="<primary key>"`, accepts rows from any other list of
 * the same kind. That is how a FAQ question is carried into another section
 * without its form being opened at all.
 *
 * The rows stay reachable without a mouse: a focused handle moves its row
 * with the arrow keys, which is the whole reason the numeric inputs could go
 * away in the first place. The keyboard never leaves its own list.
 */

import { postJson } from "../core/http";
import { queryAll } from "../core/dom";
import { toast } from "../core/toast";
import { announceHistoryChange } from "./ownHistory";

/** Address writing a new order down. */
const ORDER_URL = "/api/admin/ordering";

/** Class marking the row being dragged. */
const DRAGGING = "is-dragging";

/** Class marking the row the pointer is currently over. */
const OVER = "is-over";

/** Class marking a list a row is being carried into from another one. */
const TARGET = "is-target";

/**
 * Class on the document while a row is in the air.
 *
 * A tree has a list under every row, most of them empty, and an empty list is
 * nothing to aim at. They are drawn as a place to drop while - and only while
 * - something is being carried, so the screen stays quiet the rest of the
 * time.
 */
const SORTING = "is-sorting";

/** Answer of the ordering endpoint. */
interface OrderResponse {
    readonly reordered: number;
}

/** Return the rows of `list` in the order they are rendered. */
function rowsOf(list: HTMLElement): HTMLElement[] {
    return queryAll<HTMLElement>("[data-sortable-id]", list).filter((row) => row.parentElement === list);
}

/** Return the ids of `rows`, skipping anything without one. */
function idsOf(rows: HTMLElement[]): number[] {
    return rows.map((row) => Number.parseInt(row.dataset.sortableId ?? "", 10)).filter((id) => Number.isFinite(id));
}

/** Return the group a list stands for, or `null` when it stands for none. */
export function groupOf(list: HTMLElement): number | null {
    const parsed = Number.parseInt(list.dataset["sortableParent"] ?? "", 10);

    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Return whether a row of `from` may be carried into `into`.
 *
 * Its own list always accepts it back. Another one accepts it when both hold
 * the same kind of row and both say which group they stand for - without that
 * the server would have nothing to move the row into.
 */
export function acceptsRow(from: HTMLElement, into: HTMLElement): boolean {
    if (from === into) {
        return true;
    }

    return (
        from.dataset["sortable"] === into.dataset["sortable"] && groupOf(from) !== null && groupOf(into) !== null
    );
}

/**
 * Show or hide the message a list falls back to when it holds nothing.
 *
 * A section emptied by a drag has to say so at once rather than after the
 * page is loaded again - it is also still somewhere to drop, and an empty
 * frame says nothing about that.
 */
export function refreshEmpty(list: HTMLElement): void {
    const selector = list.dataset["sortableEmpty"] ?? "";
    if (!selector) {
        return;
    }

    const message = document.querySelector<HTMLElement>(selector);
    if (message) {
        message.hidden = rowsOf(list).length > 0;
    }
}

/** Say that the lists have changed, so what is drawn from them can follow. */
function announce(lists: readonly HTMLElement[]): void {
    for (const list of lists) {
        refreshEmpty(list);
    }

    document.dispatchEvent(new CustomEvent("sortable:moved", { bubbles: true }));
}

/** Renumber the position labels so they match what the reader sees. */
function renumber(list: HTMLElement): void {
    rowsOf(list).forEach((row, index) => {
        const label = row.querySelector("[data-sortable-position]");
        if (label) {
            label.textContent = String(index + 1);
        }
    });
}

/** Send the order of one list, saying which group it stands for when asked. */
async function sendOrder(list: HTMLElement, withGroup: boolean): Promise<void> {
    const kind = list.dataset.sortable ?? "";
    const ids = idsOf(rowsOf(list));
    const group = groupOf(list);

    // A list emptied by a drag has nothing left to order, which is not a
    // failure - the row it lost is being written down by the other one.
    if (!kind || ids.length === 0) {
        return;
    }

    await postJson<OrderResponse>(ORDER_URL, {
        kind,
        ids,
        ...(withGroup && group !== null ? { parent: group } : {}),
    });
    renumber(list);
}

/**
 * Store the order of the lists a drag touched, putting the rows back on
 * failure.
 *
 * A row carried into another list changes two of them, and both are sent: the
 * one it landed in, together with the group it now belongs to, and the one it
 * came from, so its numbering closes up again.
 */
async function persist(landed: HTMLElement, home: HTMLElement, order: HTMLElement[]): Promise<void> {
    const moved = landed !== home;
    const touched = moved ? [landed, home] : [landed];

    for (const list of touched) {
        list.setAttribute("aria-busy", "true");
    }

    try {
        await sendOrder(landed, moved);

        if (moved) {
            await sendOrder(home, false);
        }

        // The new order is in the history now; the screen stays, so the
        // panel of the history on it is told to catch up.
        announceHistoryChange();
    } catch {
        // Appending the rows in the order they had puts both halves right at
        // once: the one which left comes home, and the rest fall into place.
        for (const row of order) {
            home.appendChild(row);
        }

        for (const list of touched) {
            renumber(list);
        }

        announce(touched);
        toast(landed.dataset.sortableError ?? "Poradie sa nepodarilo uložiť.", "error");
    } finally {
        for (const list of touched) {
            list.removeAttribute("aria-busy");
        }
    }
}

/** Move `row` before or after `target`, wherever it is coming from. */
function place(list: HTMLElement, row: HTMLElement, target: HTMLElement): void {
    const rows = rowsOf(list);
    const from = rows.indexOf(row);
    const to = rows.indexOf(target);

    if (to < 0) {
        return;
    }

    // A row arriving from another list has no place in this one yet, so it
    // goes in ahead of whatever it was dropped on.
    if (from < 0) {
        target.before(row);

        return;
    }

    if (from === to) {
        return;
    }

    if (from < to) {
        target.after(row);
    } else {
        target.before(row);
    }
}

/** The row being carried, and the list it started out in. */
interface Drag {
    readonly row: HTMLElement;
    readonly home: HTMLElement;
    readonly order: HTMLElement[];
}

/**
 * The drag in progress, shared by every list on the page.
 *
 * It cannot belong to one list: a row carried from one section into another
 * raises `dragstart` on the first and `drop` on the second, and neither of
 * them can see the state of the other.
 */
let carrying: Drag | null = null;

/** Take the marks of a drag off every list and row. */
function clearMarks(): void {
    document.body.classList.remove(SORTING);

    for (const list of queryAll<HTMLElement>("[data-sortable]")) {
        list.classList.remove(TARGET);

        for (const row of rowsOf(list)) {
            row.classList.remove(OVER, DRAGGING);
        }
    }
}

/** Wire the pointer dragging of one list. */
function wireDragging(list: HTMLElement): void {
    list.addEventListener("dragstart", (event) => {
        const row = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-sortable-id]") ?? null;
        if (!row || row.parentElement !== list) {
            return;
        }

        // Anything which is not the handle stays a plain row: a drag begun by
        // grabbing a link or a paragraph is refused outright.
        if (!row.draggable) {
            event.preventDefault();

            return;
        }

        carrying = { row, home: list, order: rowsOf(list) };
        row.classList.add(DRAGGING);
        document.body.classList.add(SORTING);
        event.dataTransfer?.setData("text/plain", row.dataset.sortableId ?? "");

        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = "move";
        }
    });

    list.addEventListener("dragover", (event) => {
        if (!carrying || !acceptsRow(carrying.home, list)) {
            return;
        }

        event.preventDefault();

        // A tree nests one list inside another, so the same event reaches
        // both. The innermost one is the one the pointer is really over, and
        // letting it bubble would have the parent take the row straight back.
        event.stopPropagation();
        list.classList.toggle(TARGET, list !== carrying.home);

        const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-sortable-id]") ?? null;

        // A list holding nothing yet is still somewhere to land.
        if (!target || target.parentElement !== list) {
            if (carrying.row.parentElement !== list) {
                list.appendChild(carrying.row);
            }

            return;
        }

        if (target === carrying.row) {
            return;
        }

        for (const row of rowsOf(list)) {
            row.classList.toggle(OVER, row === target);
        }

        place(list, carrying.row, target);
    });

    const finish = (): void => {
        if (!carrying) {
            document.body.classList.remove(SORTING);

            return;
        }

        const { row, home, order } = carrying;
        const landed = row.parentElement ?? home;

        carrying = null;
        clearMarks();

        const unchanged = landed === home && idsOf(rowsOf(home)).join(",") === idsOf(order).join(",");
        if (!unchanged) {
            announce(landed === home ? [home] : [landed, home]);
            void persist(landed, home, order);
        }

        row.focus({ preventScroll: true });
    };

    list.addEventListener("drop", (event) => {
        event.preventDefault();
        event.stopPropagation();
        finish();
    });

    list.addEventListener("dragend", finish);
}

/** Wire the keyboard reordering of one list. */
function wireKeyboard(list: HTMLElement): void {
    list.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
            return;
        }

        const handle = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-sortable-handle]");
        const row = handle?.closest<HTMLElement>("[data-sortable-id]") ?? null;
        if (!row || row.parentElement !== list) {
            return;
        }

        event.preventDefault();

        const before = rowsOf(list);
        const index = before.indexOf(row);
        const target = event.key === "ArrowUp" ? before[index - 1] : before[index + 1];
        if (!target) {
            return;
        }

        if (event.key === "ArrowUp") {
            target.before(row);
        } else {
            target.after(row);
        }

        handle?.focus({ preventScroll: true });
        announce([list]);
        void persist(list, list, before);
    });
}

/**
 * Let a row be picked up, but only by its handle.
 *
 * A row which is always draggable cannot have its text selected and starts a
 * drag from anywhere in it, including the links and buttons inside. So it is
 * made draggable while the handle is held and plain again the moment it is
 * let go.
 */
function wireHandles(list: HTMLElement): void {
    const release = (): void => {
        for (const row of rowsOf(list)) {
            row.draggable = false;
        }
    };

    list.addEventListener("pointerdown", (event: PointerEvent) => {
        const handle = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-sortable-handle]");
        const row = handle?.closest<HTMLElement>("[data-sortable-id]") ?? null;

        release();

        if (row && row.parentElement === list) {
            row.draggable = true;
        }
    });

    list.addEventListener("pointerup", release);
    list.addEventListener("dragend", release);
}

/** Wire one list up for the pointer and for the keyboard. */
function initList(list: HTMLElement): void {
    wireHandles(list);
    wireDragging(list);
    wireKeyboard(list);
}

/** Wire every sortable list of the current page. */
export function initSortables(): void {
    carrying = null;

    for (const list of queryAll<HTMLElement>("[data-sortable]")) {
        initList(list);
        refreshEmpty(list);
    }
}
